import { useState, useCallback, useRef, useEffect } from 'react'
import { reviewCV } from '../services/api/claude'
import { useTranslation } from '../contexts/LanguageContext'
import type { CV } from '../types/cv'
import type { CVReviewResponse } from '../types/api'
import type { Translations } from '../i18n/types'

interface UseCVReviewReturn {
  isReviewing: boolean
  review: CVReviewResponse | null
  error: string | null
  startReview: (cv: CV, language?: 'es' | 'en') => Promise<CVReviewResponse | null>
  reset: () => void
}

function extractErrorMessage(err: unknown, t: Translations): string {
  if (err instanceof Error) {
    const message = err.message

    if (message.includes('unauthenticated')) {
      return t.aiReview.loginToReview
    }
    if (message.includes('resource-exhausted')) {
      return message.replace(/^.*?:/, '').trim() || t.aiReview.rateLimitReached
    }
    if (message.includes('internal')) {
      return t.aiReview.reviewProcessError
    }

    return message
  }

  return t.aiReview.unknownReviewError
}

export function useCVReview(): UseCVReviewReturn {
  const { t } = useTranslation()
  const tRef = useRef(t)
  useEffect(() => { tRef.current = t }, [t])

  const [isReviewing, setIsReviewing] = useState(false)
  const [review, setReview] = useState<CVReviewResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  const startReview = useCallback(
    async (cv: CV, language: 'es' | 'en' = 'es'): Promise<CVReviewResponse | null> => {
      setIsReviewing(true)
      setError(null)

      try {
        const result = await reviewCV(cv, language)
        setReview(result)
        return result
      } catch (err) {
        const errorMessage = extractErrorMessage(err, tRef.current)
        setError(errorMessage)
        return null
      } finally {
        setIsReviewing(false)
      }
    },
    []
  )

  const reset = useCallback(() => {
    setReview(null)
    setError(null)
  }, [])

  return {
    isReviewing,
    review,
    error,
    startReview,
    reset,
  }
}
